import { VISUAL_HELPERS } from "../utils";

export const pathRegex = /^\/([a-zA-Z-]{2,})(?:\/(.*))?$/;

const DANISH_LOCALES = new Set(["da", "danish"]);

export const getDefaultPath = (local) =>
  DANISH_LOCALES.has((local || "").toLowerCase()) ? "billet" : "ticket";

const getPathSegment = (match) => (match[2] || "").replace(/\/$/, "");

const getFlow = () => {
  const match = window.location.pathname.match(pathRegex) || [];
  const local = match[1] || "english";
  const path = getPathSegment(match) || getDefaultPath(local);

  return `${local}/${path}`;
};

const initiateCart = async () => {
  const uri = `${window.BASE_URL}api/cart`;
  const match = window.location.pathname.match(pathRegex) || [];
  const locale = match[1] || "english";

  const response = await fetch(uri, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ locale }),
  });

  if (!response.ok) {
    throw new Error(`Failed to initiate cart: ${response.status}`);
  }

  const webApiData = await response.json();
  return {
    data: {
      cartMask: webApiData.id,
      checkoutUrl: webApiData.checkoutUrl,
    },
  };
};

const restoreCart = async (cartId) => {
  const uri = `${window.BASE_URL}api/cart?cartId=${cartId}`;

  const response = await fetch(uri);
  const webApiData = await response.json().catch(() => null);

  return {
    data: webApiData,
    status: response.status,
    ok: response.ok,
  };
};

const addItemsToCart = async (state, areExtra = false) => {
  const uri = `${window.BASE_URL}api/cart/add`;

  const selectedTickets = areExtra
    ? []
    : state.summary.selectedAdmissionTickets;
  const selectedAddonTickets = state.summary.selectedAddonTickets;
  const selectedDate = state.dates.find(
    (item) => item.date === state.summary.selectedDate
  );
  const selectedTimeslot = state.summary.selectedTimeslot;

  if (!selectedTickets.length && !selectedAddonTickets.length) {
    return true;
  }
  const payload = {
    cartId: localStorage.getItem("cart"),
    flow: getFlow(),
    lines: [
      ...selectedTickets.map((item) => {
        const line = {
          quantity: item.qty,
          productId: item.id,
          date: selectedDate.formatted_date,
          admissionCode: "VISITCARLSBERG",
          scheduleNumber: selectedTimeslot,
        };

        const admissionTicket = state.admission_tickets?.find(
          (ticket) => ticket.id === item.id
        );
        const ageRange = admissionTicket?.age_range;
        const ccCards = state.summary.selectedCCCards?.filter(
          (cc_card) => cc_card.age.value === ageRange
        );

        if (ccCards?.length) {
          line.cityCard = ccCards.map((cc) => cc.cardNumber).join(",");
        }

        return line;
      }),
      ...Object.values(selectedAddonTickets).map((item, index) => ({
        quantity: item.qty,
        productId: Object.keys(selectedAddonTickets)[index],
        date: selectedDate.formatted_date,
        admissionCode: state.addons.find(
          (addon) => addon.id === Object.keys(selectedAddonTickets)[index]
        ).admission_code,
        scheduleNumber: item.entry_no,
      })),
    ],
  };

  try {
    const response = await fetch(uri, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    return response.json().then((res) => res);
  } catch (error) {
    console.error(error);
  }
};

const updateCart = async (cartId, lines) => {
  const uri = `${window.BASE_URL}api/cart/lines/update`;

  const payload = {
    cartId,
    lines,
  };

  try {
    const response = await fetch(uri, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    return response.json().then((res) => res);
  } catch (error) {
    throw error;
  }
};

const removeFromCart = async (cartId, cartLineIds) => {
  const uri = `${window.BASE_URL}api/cart/lines/remove`;

  const payload = {
    cartId,
    cartLineIds,
  };

  try {
    const response = await fetch(uri, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    return response.json().then((res) => res);
  } catch (error) {
    throw error;
  }
};

const createCartItemKey = (productId, date, scheduleNumber, admissionCode) =>
  `${productId}-${date}-${scheduleNumber}-${admissionCode}`;

const mapCurrentStateToCartItems = (state) => {
  const items = new Map();

  const selectedTickets = state.summary.selectedAdmissionTickets || [];
  const selectedAddonTickets = state.summary.selectedAddonTickets || {};
  const selectedDate = state.dates.find(
    (item) => item.date === state.summary.selectedDate
  );

  const selectedTimeslot = state.summary.selectedTimeslot;

  selectedTickets.forEach((ticket) => {
    const key = createCartItemKey(
      ticket.id,
      selectedDate?.formatted_date,
      selectedTimeslot,
      "VISITCARLSBERG"
    );

    const item = {
      productId: ticket.id,
      quantity: ticket.qty,
      date: selectedDate?.formatted_date,
      admissionCode: "VISITCARLSBERG",
      scheduleNumber: selectedTimeslot,
      type: "admission",
    };

    const admissionTicket = state.admission_tickets?.find(
      (t) => t.id === ticket.id
    );
    const ageRange = admissionTicket?.age_range;
    const ccCards = state.summary.selectedCCCards?.filter(
      (c) => c.age.value === ageRange
    );

    if (ccCards?.length) {
      item.cityCard = ccCards.map((cc) => cc.cardNumber).join(",");
    }

    items.set(key, item);
  });

  Object.entries(selectedAddonTickets).forEach(([addonId, item]) => {
    const addon = state.addons.find((a) => a.id === addonId);
    const key = createCartItemKey(
      addonId,
      selectedDate?.formatted_date,
      item.entry_no,
      addon?.admission_code
    );
    items.set(key, {
      productId: addonId,
      quantity: item.qty,
      date: selectedDate?.formatted_date,
      admissionCode: addon?.admission_code,
      scheduleNumber: item.entry_no,
      type: "addon",
    });
  });

  return items;
};

const mapCartLinesToItems = (cartLines) => {
  const items = new Map();

  if (!Array.isArray(cartLines)) {
    return items;
  }

  cartLines.forEach((line) => {
    const productId = line.merchandise?.product?.id;
    const quantity = line.quantity;
    const attributes = line.attributes;

    if (!productId || !Array.isArray(attributes) || attributes.length === 0) {
      return;
    }

    const getAttributeValue = (key) => {
      const attr = attributes.find((a) => a.key === key);
      return attr?.value;
    };

    const scheduleNumber = getAttributeValue("_schedule_number");
    const admissionCode = getAttributeValue("_admission_code");
    const date = getAttributeValue("_schedule_start_date");

    if (!scheduleNumber || !admissionCode || !date) {
      console.warn("Cart line missing required attributes:", line);
      return;
    }

    const key = createCartItemKey(
      productId,
      date,
      scheduleNumber,
      admissionCode
    );

    items.set(key, {
      lineId: line.id,
      productId,
      quantity,
      date,
      admissionCode,
      scheduleNumber,
    });
  });

  return items;
};

const syncCart = async (state) => {
  if (!state || !state.summary) {
    throw new Error("Invalid state provided to syncCart");
  }

  const cartId = localStorage.getItem("cart");
  if (!cartId) {
    throw new Error("No cart ID found");
  }

  const currentItems = mapCurrentStateToCartItems(state);
  const existingItems = mapCartLinesToItems(state.cart || []);

  const toRemove = [];
  const toUpdate = [];
  const toAdd = [];
  const results = {
    removed: null,
    updated: null,
    added: null,
  };

  existingItems.forEach((existingItem, key) => {
    const currentItem = currentItems.get(key);

    if (!currentItem) {
      toRemove.push(existingItem.lineId);
    } else if (currentItem.quantity !== existingItem.quantity) {
      if (currentItem.quantity === 0) {
        toRemove.push(existingItem.lineId);
      } else {
        toUpdate.push({
          lineId: existingItem.lineId,
          quantity: currentItem.quantity,
        });
      }
    }
  });

  currentItems.forEach((currentItem, key) => {
    if (!existingItems.has(key) && currentItem.quantity > 0) {
      toAdd.push(currentItem);
    }
  });

  if (toRemove.length === 0 && toUpdate.length === 0 && toAdd.length === 0) {
    try {
      const restored = await API.restoreCart(cartId);

      if (
        !restored?.ok ||
        restored?.status === 404 ||
        restored?.data?.error === "Cart not found"
      ) {
        console.warn(
          "syncCart: cart not found after operations, creating a new cart"
        );
        const newCart = await API.initiateCart();
        const newMask = newCart?.data?.cartMask || "";
        try {
          localStorage.setItem("cart", newMask);
        } catch (err) {
          console.error("Failed to save new cart to localStorage:", err);
        }

        return {
          success: true,
          operations: { toRemove, toUpdate, toAdd },
          results,
          updatedCart: newCart.data,
        };
      }

      return {
        success: true,
        operations: { toRemove, toUpdate, toAdd },
        results,
        updatedCart: restored.data,
      };
    } catch (error) {
      console.error("Error syncing cart:", error);
      throw error;
    }
  }

  try {
    if (toRemove.length > 0) {
      results.removed = await removeFromCart(cartId, toRemove);
    }

    if (toUpdate.length > 0) {
      results.updated = await updateCart(cartId, toUpdate);
    }

    if (toAdd.length > 0) {
      const lines = toAdd.map((item) => ({
        quantity: item.quantity,
        productId: item.productId,
        date: item.date,
        admissionCode: item.admissionCode,
        scheduleNumber: item.scheduleNumber,
        ...(item.cityCard && { cityCard: item.cityCard }),
      }));

      const payload = {
        cartId,
        flow: getFlow(),
        lines,
      };

      const uri = `${window.BASE_URL}api/cart/add`;
      const response = await fetch(uri, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to add items to cart: ${response.status}`);
      }

      results.added = await response.json();
    }

    try {
      const restored = await API.restoreCart(cartId);

      if (
        !restored?.ok ||
        restored?.status === 404 ||
        restored?.data?.error === "Cart not found"
      ) {
        console.warn(
          "syncCart: cart not found after operations, creating a new cart"
        );
        const newCart = await API.initiateCart();
        const newMask = newCart?.data?.cartMask || "";
        try {
          localStorage.setItem("cart", newMask);
        } catch (err) {
          console.error("Failed to save new cart to localStorage:", err);
        }

        return {
          success: true,
          operations: { toRemove, toUpdate, toAdd },
          results,
          updatedCart: newCart.data,
        };
      }

      return {
        success: true,
        operations: { toRemove, toUpdate, toAdd },
        results,
        updatedCart: restored.data,
      };
    } catch (error) {
      console.error("Error syncing cart:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error syncing cart:", error);
    throw error;
  }
};

const getActivitiesOnlyFlowCartPayload = (state) => {
  const cartItemsPayload = [];

  const { selectedDate, selectedAddonTickets } = state.summary;

  for (const [addonId, selectionInformation] of Object.entries(
    selectedAddonTickets
  )) {
    const addon = state.addons.find((adn) => adn.id.toString() === addonId);

    const addonTimeslot = addon.dates
      .find((date) => date.date === selectedDate)
      ?.timeslots?.find(
        (timeslot) => timeslot?.entry_no === selectionInformation?.entry_no
      );

    cartItemsPayload.push({
      sku: state.package_sku,
      qty: selectionInformation.qty,
      extension_attributes: {
        ticket_purchase: {
          id: addon.id,
          quantity: 1,
          admissions: [
            {
              admission_code: addon.admission_code,
              start_utc_timestamp: addonTimeslot?.timestamp_in_milis,
              entry_no: addonTimeslot?.entry_no,
            },
          ],
        },
        currency_code: state.summary.selectedCurrency.id,
      },
    });
  }

  return cartItemsPayload;
};

const getAdmissionFlowCartPayload = (state) => {
  const cartItemsPayload = [];
  const {
    selectedAddonTickets,
    selectedAdmissionTickets,
    selectedCurrency,
    selectedDate,
    selectedTimeslot,
  } = state.summary;

  if (selectedTimeslot === null) {
    return cartItemsPayload;
  }

  for (const admissionTicket of selectedAdmissionTickets) {
    const requiredAdmissions = admissionTicket.admission.filter(
      ({ selection_type }) => selection_type === "Required"
    );
    const selectedDateInfo = state.dates.find(
      (date) => date.date === selectedDate
    );

    const admissionPriceInfo = selectedDateInfo?.prices.find(
      (priceInfo) => priceInfo.product_sku === admissionTicket.productSku
    );

    const admissionPayload = [];
    const discount = Math.abs(admissionPriceInfo?.discount || 0);

    requiredAdmissions.forEach((admission) => {
      const timeslotsForAdmission = admissionPriceInfo?.timeslots?.filter(
        (timeslot) => timeslot.admission_code === admission.code
      );

      if (timeslotsForAdmission?.length === 1) {
        admissionPayload.push({
          admission_code: admission.code,
          start_utc_timestamp: selectedDate,
          entry_no: timeslotsForAdmission?.[0]?.entry_no,
        });
      }

      if (timeslotsForAdmission?.length > 1) {
        const specificTimeslot = timeslotsForAdmission.find(
          (timeslot) => timeslot.entry_no === selectedTimeslot
        );

        admissionPayload.push({
          admission_code: admission.code,
          start_utc_timestamp: selectedDate,
          entry_no: specificTimeslot?.entry_no,
        });
      }
    });

    for (const [addonId, selectionInformation] of Object.entries(
      selectedAddonTickets
    )) {
      const addon = state.addons.find((adn) => adn.id.toString() === addonId);

      const allowedForAdmissionTicket =
        admissionTicket.admission.find(
          (checkAdmission) => checkAdmission.code === addon.admission_code
        ) !== undefined;

      const addonTimeslot = addon.dates
        .find((date) => date.date === selectedDate)
        ?.timeslots?.find(
          (timeslot) => timeslot?.entry_no === selectionInformation?.entry_no
        );

      if (allowedForAdmissionTicket && addonTimeslot) {
        admissionPayload.push({
          admission_code: addon.admission_code,
          start_utc_timestamp: addonTimeslot?.timestamp_in_milis,
          entry_no: addonTimeslot?.entry_no,
        });
      }
    }

    cartItemsPayload.push({
      sku: state.package_sku,
      qty: admissionTicket.qty,
      extension_attributes: {
        ticket_purchase: {
          id: admissionTicket.id,
          quantity: 1,
          admissions: admissionPayload,
          addons: [],
        },
        currency_code: selectedCurrency.id,
        discount: VISUAL_HELPERS.displayPriceBasedOnCurrency(
          discount,
          selectedCurrency
        ),
      },
    });
  }

  // Special events
  for (const [addonId, selectionInformation] of Object.entries(
    selectedAddonTickets
  )) {
    const addon = state.addons.find((adn) => adn.id.toString() === addonId);

    const addonAdmissionPair = selectedAdmissionTickets.find(
      (admissionTicket) =>
        admissionTicket.admission.some(
          (adm) => adm.code === addon.admission_code
        )
    );

    const addonTimeslot = addon.dates
      .find((date) => date.date === selectedDate)
      ?.timeslots?.find(
        (timeslot) => timeslot?.entry_no === selectionInformation?.entry_no
      );

    if (!addonAdmissionPair) {
      const addonEntryNo = addonTimeslot?.entry_no;
      const addonTimestamp = addonTimeslot?.timestamp_in_milis;
      const addonQty = selectionInformation.qty;

      cartItemsPayload.push({
        sku: state.package_sku,
        qty: addonQty,
        extension_attributes: {
          ticket_purchase: {
            id: addon.id,
            quantity: 1,
            admissions: [
              {
                admission_code: addon.admission_code,
                start_utc_timestamp: addonTimestamp,
                entry_no: addonEntryNo,
              },
            ],
            addons: [],
          },
          currency_code: state.summary.selectedCurrency.id,
        },
      });
    }
  }

  return cartItemsPayload;
};

const getInitialData = async (mainProductId, pathOverride) => {
  const uri = `${window.BASE_URL}api/init`;

  const match = window.location.pathname.match(pathRegex) || [];
  const local = match[1] || "" || "english";
  const path = pathOverride || getPathSegment(match) || getDefaultPath(local);
  const response = await fetch(uri, {
    method: "POST",
    body: JSON.stringify({
      path,
      local,
    }),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    const err = new Error(`Init request failed (${response.status})`);
    err.status = response.status;
    err.body = body;
    throw err;
  }

  const webApiData = await response.json();
  const returnWebApiData = {
    data: webApiData,
  };
  return returnWebApiData;
};

const getGiftcard = async (local) => {
  const uri = `${window.BASE_URL}api/giftcard?local=${encodeURIComponent(local)}`;
  const response = await fetch(uri);
  return response.json();
};

const addGiftCardToCart = async (variantId, quantity = 1) => {
  const uri = `${window.BASE_URL}api/cart/add`;

  const match = window.location.pathname.match(pathRegex) || [];
  const local = match[1] || "en";
  const giftCardSlug = (match[2] || "gift-card").split("/")[0];

  const payload = {
    cartId: localStorage.getItem("cart"),
    flow: `${local}/${giftCardSlug}`,
    lines: [{ quantity, productId: variantId }],
  };

  const response = await fetch(uri, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return response.json();
};

const getPackageData = async (
  summary,
  mode,
  mainProductId,
  fromDate,
  toDate
) => {
  const uri = `${window.BASE_URL}api/ticket/schedules`;

  const selectedAdmissionTickets = [];
  summary.selectedAdmissionTickets?.forEach((ticket) => {
    const payload = {
      product_sku: ticket.productSku,
      qty: ticket.qty,
    };

    if (fromDate && toDate) {
      payload.startDate = fromDate;
      payload.endDate = toDate;
    }

    selectedAdmissionTickets.push(payload);
  });

  const payload = {
    tickets: selectedAdmissionTickets,
    mode,
    id: mainProductId,
  };

  const packageData = await fetch(uri, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })
    .then((response) => response.json())
    .catch((error) => console.error(error));

  const returnPackageData = {
    data: packageData,
  };
  return returnPackageData;
};

const getActivitiesForDate = async (
  fromDate,
  toDate,
  mainProductId,
  tickets
) => {
  const uri = `${window.BASE_URL}api/ticket/activities`;

  const match = window.location.pathname.match(pathRegex) || [];
  const local = match[1] || "" || "english";
  const path = getPathSegment(match) || getDefaultPath(local);

  const payload = {
    id: mainProductId,
    path,
    local,
  };

  if (tickets && tickets.length > 0) {
    payload.tickets = tickets.map((ticket) => ({
      product_sku: ticket.productSku,
      qty: ticket.qty,
    }));
  }

  if (
    fromDate !== null &&
    typeof fromDate === "object" &&
    toDate !== null &&
    typeof toDate === "object"
  ) {
    payload.startDate = VISUAL_HELPERS.displayDateAsIsoDate(fromDate.date);
    payload.endDate = VISUAL_HELPERS.displayDateAsIsoDate(toDate.date);
  } else {
    payload.startDate = fromDate;
    payload.endDate = toDate;
  }

  const activitiesData = await fetch(uri, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })
    .then((response) => response.json())
    .catch((error) => console.eror(error));

  const returnActivitiesData = {
    data: activitiesData,
  };
  return returnActivitiesData;
};

const validateCCCards = async (payload) => {
  const cards = (
    Array.isArray(payload?.cityCard)
      ? payload.cityCard
      : String(payload?.cityCard ?? "").split(",")
  )
    .map((x) => `cityCard=${String(x).trim()}`)
    .join("&");

  const url = `${window.BASE_URL}api/citycard/validate?${cards}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then((res) => res.json())
    .catch((error) => console.error(error));

  return response;
};

const extendReservationForCheckout = async (cartId) => {
  const uri = `${window.BASE_URL}api/cart/reservation/extend`;

  const payload = {
    cartId,
  };

  try {
    const response = await fetch(uri, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to extend reservation: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error extending reservation:", error);
    throw error;
  }
};

const checkUserLoginSession = async () => {
  const match = window.location.pathname.match(pathRegex) || [];
  const locale = match[1] || "en";
  const path = match[2] || "";
  const uri = `${window.LOGIN_WORKER_URL}api/session?locale=${encodeURIComponent(locale)}${path ? `&path=${encodeURIComponent(path)}` : ""}`;

  try {
    const response = await fetch(uri, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`No logged in user: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error checking login status:", error);
    throw error;
  }
};

const applyDiscount = async (cartId, discountCode) => {
  const uri = `${window.BASE_URL}api/cart/discount/apply`;

  try {
    const response = await fetch(uri, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        cartId,
        discountCode,
      }),
    });

    const result = await response.json();

    return {
      data: result,
      status: response.status,
      ok: response.ok,
    };
  } catch (error) {
    console.error("Error applying discount:", error);
    return {
      data: null,
      status: 500,
      ok: false,
      error: error.message,
    };
  }
};

export const API = {
  addItemsToCart,
  addGiftCardToCart,
  initiateCart,
  getInitialData,
  getGiftcard,
  getPackageData,
  getActivitiesForDate,
  restoreCart,
  validateCCCards,
  updateCart,
  removeFromCart,
  syncCart,
  extendReservationForCheckout,
  checkUserLoginSession,
  applyDiscount,
};
